'use client';

import { sendGTMEvent } from '@next/third-parties/google';
import { useEffect } from 'react';
import { usePathname } from '@navigation';
import { PageType } from '@providers/analytics/analytics.types';

// Generic page-view dispatcher. Dropped once into the locale layout and
// fires a `page_view` dataLayer event on every route change, with
// metadata derived from the URL. Phase B+ baseline — enough structure
// for GTM to do useful slicing without atom needing per-template
// instrumentation everywhere.
//
// Per-template enrichment (publishedDate, author, fixture id, team
// name, tags, etc.) is added by per-route `<AnalyticsPageView>`
// components inside specific page templates. Those routes are listed
// in `SKIP_AUTO_PATTERNS` below — AutoPageView skips firing for them
// so we don't double-emit page_view with stale baseline data.
//
// `usePathname` from `@navigation` is next-intl's locale-aware variant
// — returns the pathname WITHOUT the locale prefix.

// Paths that have their own per-template <AnalyticsPageView> instance
// firing a richer page_view event. AutoPageView skips these to avoid
// double-counting.
const SKIP_AUTO_PATTERNS: readonly RegExp[] = [
  // /news/[article-slug] — handled by AnalyticsPageView inside
  // ArticleTemplate. Negative lookahead excludes the category / tag /
  // listing sub-routes which look like 2-segment URLs but aren't
  // article details.
  /^\/news\/(?!category$|tag$|listing$)[^/]+$/,
];

export function derivePageType(pathname: string): PageType {
  const path = pathname || '/';

  if (path === '/' || path === '') return 'home';

  if (path.startsWith('/news')) return 'news';
  if (path.startsWith('/video')) return 'video';

  if (path.startsWith('/matches')) {
    // /matches/[team] (no extra segment)                  → fixture
    // /matches/[team]/(fixtures|results|table)/...        → fixture
    // /matches/[team]/[fixture-id-or-slug]                → match_centre
    const segments = path.split('/').filter(Boolean);
    if (
      segments.length >= 3 &&
      !['fixtures', 'results', 'table'].includes(segments[2] ?? '')
    ) {
      return 'match_centre';
    }
    return 'fixture';
  }

  if (path.startsWith('/teams')) {
    if (path.endsWith('/all-players')) return 'squad';
    if (path.endsWith('/roll-of-honour')) return 'team';
    // /teams/[team]                       → team
    // /teams/[team]/[profile]             → squad (player profile)
    // /teams/youth/[team]                 → team
    // /teams/youth/[team]/[profile]       → squad
    const segments = path.split('/').filter(Boolean);
    const youthOffset = segments[1] === 'youth' ? 1 : 0;
    if (segments.length >= 3 + youthOffset) return 'squad';
    return 'team';
  }

  return 'content';
}

const AutoPageView = () => {
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Skip if a per-template AnalyticsPageView is going to fire its own
    // richer page_view for this route.
    if (SKIP_AUTO_PATTERNS.some((pattern) => pattern.test(pathname))) {
      return;
    }

    sendGTMEvent({
      event: 'page_view',
      page_title: document.title,
      page_path: pathname,
      page_location: window.location.href,
      page_type: derivePageType(pathname),
      previous_page: document.referrer || undefined,
    });
  }, [pathname]);

  return null;
};

export default AutoPageView;
